import axios from "axios";

const BASE_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:8080";

interface UploadRequestResponse {
  data: {
    uploadKey: string;
    uploadUrl: string;
    sessionId: string;
    expiresAt: string;
  };
}

interface UploadResult {
  success: boolean;
  uploadKey?: string;
  sessionId?: string;
  expiresAt?: string;
  fileUploadId?: string;
  fileUrl?: string;
  error?: string | Record<string, unknown>;
}

interface CompleteFileUploadResponse {
  data: {
    fileUploadId: string;
    publicUrl: string;
    category: string;
    fileType: string;
    fileSize: number;
    filename: string;
  };
}
export const FileType = {
  VIDEO: "VIDEO",
  PDF: "PDF",
  IMAGE: "IMAGE",
  DOCUMENT: "DOCUMENT",
} as const;

export type FileType = (typeof FileType)[keyof typeof FileType];

function getFileType(mimeType: string): FileType {
  if (mimeType.startsWith("image/")) return FileType.IMAGE;
  if (mimeType.startsWith("video/")) return FileType.VIDEO;
  if (mimeType === "application/pdf") return FileType.PDF;
  return FileType.DOCUMENT;
}

export const handleFileUpload = async (
  file: File,
  category?: any
): Promise<UploadResult> => {
  try {
    const detectedFileType = getFileType(file.type);
    const fileConfig = {
      filename: file.name,
      contentType: file.type,
      fileSize: file.size,
      fileType: detectedFileType,
      category: category ? category :"GENERAL",
      needsProcessing: false,
    };

    if (file.type.startsWith("video/") ) fileConfig.needsProcessing = true;

    const {
      data: {
        data: { uploadKey, uploadUrl, sessionId, expiresAt },
      },
    }: { data: UploadRequestResponse } = await axios.post(
      `${BASE_URL}/api/upload/request2`,
      fileConfig,
      {
        withCredentials: true,
      }
    );

    const putResponse = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });
    // fetch doesn't reject on HTTP errors; without this a rejected upload went
    // on to "complete" a file that never reached storage.
    if (!putResponse.ok) {
      return {
        success: false,
        error: `Storage rejected the file (HTTP ${putResponse.status})`,
      };
    }

    const {
      data: {
        data: { fileUploadId, publicUrl },
      },
    }: { data: CompleteFileUploadResponse } = await axios.post(
      `${BASE_URL}/api/upload/complete2`,
      { sessionId },
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return {
      success: true,
      uploadKey,
      sessionId,
      expiresAt,
      fileUploadId,
      fileUrl: publicUrl,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      // Prefer the server's message (e.g. "File size cannot exceed 10MB").
      const data = error.response?.data as { message?: string | string[] } | undefined;
      const message = Array.isArray(data?.message)
        ? data?.message.join(" ")
        : data?.message;
      return {
        success: false,
        error: message || error.message,
      };
    }
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Network error while uploading",
    };
  }
};

export const getSignedURL = async (s3Key: string): Promise<string | null> => {
  try {
    const { data } = await axios.post(
      `${BASE_URL}/api/storage/file/getURL`,
      { s3Key },
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return data.signedUrl || null;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      console.error(
        "Error fetching signed URL:",
        error.response?.data || error.message
      );
    } else {
      console.error("Unknown error occurred while fetching signed URL");
    }
    return null;
  }
};

export const downloadPDF = async (
  content: string,
  filename: string,
  extension: string = "pdf"
): Promise<void> => {
  const fileDownloadData = {
    content: content,
    filename: filename.trim().split(" ").join("") + "." + extension,
  };
  const response =  await axios.post(
    `${BASE_URL}/api/upload/convert-to-pdf`,
    fileDownloadData,
    {
      withCredentials: true,
    }
  );
  return response.data;
};
